// R1b-β Step 2 Deliverable B — Public accept-invite page.
// Matches Login split-screen brand aesthetic; server-authoritative flow;
// no auto-login (user must sign in after setting password).
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCapturedParams } from "../../lib/cleanUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import PasswordInput from "../../components/PasswordInput";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck } from "lucide-react";

const LOGIN_PANEL = "/brand/login_visual_panel.jpg";
const LOGO_REVERSED = "/brand/erf_logo_horizontal_reversed.jpg";

const RULES = [
  { key: "len",  label: "At least 12 characters",           fn: (p) => p.length >= 12 },
  { key: "up",   label: "One uppercase letter",              fn: (p) => /[A-Z]/.test(p) },
  { key: "low",  label: "One lowercase letter",              fn: (p) => /[a-z]/.test(p) },
  { key: "dig",  label: "One digit",                         fn: (p) => /[0-9]/.test(p) },
  { key: "sym",  label: "One special character",             fn: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function AcceptInvite() {
  const token = useCapturedParams().token || "";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get(`/auth/invite/validate`, { params: { token } })
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ ok: false, reason: "invite_invalid_or_expired" }))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    for (const r of RULES) {
      if (!r.fn(pw)) { setErr(r.label + " is required."); return; }
    }
    setBusy(true);
    try {
      await api.post(`/auth/accept-invite`, { token, password: pw, confirm_password: pw2 });
      toast.success("Password set. Please sign in with your new credentials.");
      navigate("/login");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const invalid = !loading && (!token || !info?.ok);

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="accept-invite-page"
         style={{ background: "#F5F1E8", fontFamily: '"Inter", system-ui, sans-serif' }}>
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden" style={{ minHeight: "100vh", backgroundColor: "#0B2545" }}>
        <img src={LOGIN_PANEL} alt="" aria-hidden="true"
             style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }}/>
        <div aria-hidden="true" style={{ position: "absolute", inset: 0,
             background: "linear-gradient(180deg, rgba(11,37,69,0.65) 0%, rgba(11,37,69,0.9) 100%)" }}/>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full" style={{ color: "#F5F1E8" }}>
          <Link to="/" data-testid="accept-invite-brand-link" aria-label="ERF Enterprises home">
            <img src={LOGO_REVERSED} alt="ERF ENTERPRISES LLC" style={{ height: 56, width: "auto", display: "block" }}/>
          </Link>
          <div style={{ maxWidth: 460 }}>
            <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.32em",
                          textTransform: "uppercase", color: "#D9B87A", marginBottom: 20 }}>
              You're invited
            </div>
            <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 40, lineHeight: 1.15,
                         fontWeight: 700, color: "#F5F1E8", margin: 0, letterSpacing: "-0.01em" }}>
              Set your password and join the team.
            </h2>
            <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.65, color: "rgba(245,241,232,0.82)" }}>
              Every action inside ERF ConstructionOS is written to an immutable audit ledger. Choose a strong password below.
            </p>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6,
                        fontFamily: '"IBM Plex Mono", monospace', fontSize: 10,
                        letterSpacing: "0.24em", textTransform: "uppercase",
                        color: "rgba(245,241,232,0.55)" }}>
            <ShieldCheck size={12} color="#D9B87A"/> Audit-tracked onboarding
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6" style={{ minHeight: "100vh" }}>
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="accept-invite-form"
              style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.28em",
                          textTransform: "uppercase", color: "#B08D57", marginBottom: 8 }}>
              Accept Invite
            </div>
            <h1 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 30, fontWeight: 700,
                         color: "#0B2545", letterSpacing: "-0.01em", margin: 0, lineHeight: 1.15 }}>
              {loading ? "Checking your invite…" : invalid ? "Invalid or expired invite" : "Set your password"}
            </h1>
            {!loading && !invalid && (
              <p style={{ fontSize: 13.5, color: "#1F2937", opacity: 0.75, marginTop: 8 }}>
                Signing up as <strong>{info?.email}</strong> · Role: <strong>{info?.role_label}</strong>
              </p>
            )}
          </div>

          {invalid ? (
            <div data-testid="accept-invite-invalid"
                 style={{ background: "#FDECEC", border: "1px solid #F5B7B1", borderLeft: "3px solid #A93226",
                          color: "#7B241C", padding: "12px 14px", borderRadius: 3, fontSize: 13 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }}/>
                <div>
                  <div style={{ fontWeight: 600 }}>We couldn't validate this invite.</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    The link may have expired or already been used. Please ask your administrator to resend the invite.
                  </div>
                  <Link to="/login" data-testid="accept-invite-back-to-login"
                        style={{ marginTop: 10, display: "inline-block", color: "#0B2545", textDecoration: "underline", fontSize: 12 }}>
                    Back to sign-in
                  </Link>
                </div>
              </div>
            </div>
          ) : loading ? null : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: "#0B2545", opacity: 0.75 }}>
                  New password
                </Label>
                <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)}
                               testId="accept-invite-password" autoComplete="new-password"
                               style={{ background: "#FFFFFF", border: "1px solid #E5E1DA", height: 44, color: "#0B2545" }}/>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: "#0B2545", opacity: 0.75 }}>
                  Confirm password
                </Label>
                <PasswordInput required value={pw2} onChange={(e) => setPw2(e.target.value)}
                               testId="accept-invite-confirm" autoComplete="new-password"
                               style={{ background: "#FFFFFF", border: "1px solid #E5E1DA", height: 44, color: "#0B2545" }}/>
              </div>
              <div data-testid="accept-invite-rules" style={{ fontSize: 12, color: "#1F2937" }}>
                {RULES.map((r) => (
                  <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2,
                                            color: r.fn(pw) ? "#1E7E34" : "#7A5C1E" }}>
                    <span aria-hidden="true">{r.fn(pw) ? "✓" : "·"}</span> {r.label}
                  </div>
                ))}
              </div>

              {err && (
                <div data-testid="accept-invite-error"
                     style={{ background: "#FDECEC", border: "1px solid #F5B7B1", borderLeft: "3px solid #A93226",
                              color: "#7B241C", padding: "10px 12px", borderRadius: 3, fontSize: 13 }}>
                  {err}
                </div>
              )}

              <Button type="submit" disabled={busy} data-testid="accept-invite-submit"
                      style={{ width: "100%", height: 46, background: "#B08D57", color: "#0B2545",
                               fontSize: 12, fontWeight: 700, letterSpacing: "0.18em",
                               textTransform: "uppercase", borderRadius: 3, border: "none" }}>
                {busy ? "Setting…" : "Set password & continue"}
              </Button>

              <Link to="/login" data-testid="accept-invite-login-link"
                    style={{ fontSize: 12, color: "#0B2545", opacity: 0.75, textAlign: "center" }}>
                Already have an account? Sign in
              </Link>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
